import { ManageableItemsList, RichTextarea } from '@payaca/ambrosia';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import {
  getStoredPercentageDue,
  isFixedDepositStage,
} from '@payaca/helpers/storedPaymentSchedule';
import { getStoredStagesWithGrossValues } from '@payaca/helpers/paymentScheduleHelper';
import {
  StoredPaymentSchedule,
  StoredPaymentScheduleStage,
} from '@payaca/types/payment-schedule';
import { Nullish } from '@payaca/utilities/types';
import { useMemo } from 'react';
import { useRegion } from '@/api/rest/users/useRegion';
import useDescribeFixedDeposit from '@/hooks/useDescribeFixedDeposit';

type PaymentScheduleStageData = Pick<
  StoredPaymentScheduleStage,
  'description'
> & {
  percentageDue: string;
  isDeposit: 'Yes' | 'No';
  value?: number;
};

type Props = {
  paymentSchedule: Nullish<StoredPaymentSchedule>;
  projectValue?: number;
};
const PaymentScheduleDefinitionReadView = ({
  paymentSchedule,
  projectValue,
}: Props) => {
  const region = useRegion();
  const describeFixedDeposit = useDescribeFixedDeposit();

  const tableData = useMemo((): PaymentScheduleStageData[] => {
    if (!paymentSchedule?.stages) return [];

    return getStoredStagesWithGrossValues(
      paymentSchedule,
      projectValue || 0
    ).map(({ stage, value }) => ({
      description: stage.description,
      percentageDue: isFixedDepositStage(stage)
        ? describeFixedDeposit(stage.basis)
        : `${getStoredPercentageDue(stage)}%`,
      isDeposit: stage.type === 'deposit' ? 'Yes' : 'No',
      value: value ?? undefined,
    }));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'projectValue'
  }, [paymentSchedule, describeFixedDeposit]);

  return (
    <ManageableItemsList>
      <ManageableItemsList.Table
        uniqueKey="description"
        items={tableData}
        hideEmptyState
        doNotUseFooterRow={
          paymentSchedule?.explainer ? (
            <div className="mx-6 my-2">
              <RichTextarea readOnly value={paymentSchedule.explainer} />
            </div>
          ) : undefined
        }
      >
        <ManageableItemsList.Table.Column<
          PaymentScheduleStageData,
          'description'
        >
          field="description"
          header="Stage"
          className="!whitespace-normal"
        />
        <ManageableItemsList.Table.Column<
          PaymentScheduleStageData,
          'percentageDue'
        >
          field="percentageDue"
          header="Due"
        />
        <ManageableItemsList.Table.Column<PaymentScheduleStageData, 'isDeposit'>
          field="isDeposit"
          header="Deposit?"
        />
        {!!projectValue && (
          <ManageableItemsList.Table.Column<PaymentScheduleStageData, 'value'>
            field="value"
            header="Value"
            render={(field) => field && currencyPrice(field, region)}
          />
        )}
      </ManageableItemsList.Table>
    </ManageableItemsList>
  );
};

export default PaymentScheduleDefinitionReadView;
